import * as Constants from "../../config/constants/Constants.js";
import { getLanguage } from "../../config/language/language.js";

import { queryUserMe } from "./userQueries.js";
import { queryExaminationStatistics, queryPatientStatistics, queryProgramStatus } from "./backendQueries";
import { checkJWTvalidity, sessionExpired } from "./authQueries.js";
import { queryExaminations } from "./examinationQueries.js";
import { queryPatients } from "./patientQueries.js";

import { isArray } from "../shared/miscUtils";
import { updateJWT } from "../auth/userAuthUtils.js";

/**
 * Check if JWT is still valid, if not, log out and redirect to sign in page.
 */
export const executeFunctionsAfterPageLoad = (props) => {
    checkJWTvalidity(props);
};

/**
 * Functions that should query data from the backend.
 */
export const executeFunctionsAfterConnectionEstablished = (props) => {
    queryProgramStatus(props);
};

/**
 * Executed during after successful validation and sign in.
 */
export const successfulJwtValidation = (props, jwt) => {
    const newJwt = jwt ?? props.user.userJWT;

    updateJWT(props, newJwt); // Re-save user-related localstorage variables from the valid JWT token to guard against tampering.

    props.user.setIsLoggedIn(true);
    props.server.setIsConnectionToServerEstablished(true);
};

/**
 * Here are functions that should be executed after the user has been verified and logged in.
 * These usually need authentication.
 */
export const executeFunctionsAfterLogin = (props) => {
    queryUserMe(props);

    queryExaminationStatistics(props);
    queryPatientStatistics(props);

    queryExaminations(props, { page: 0, pageSize: 10 });
    queryPatients(props, { page: 0, pageSize: 10 });
};

/**
 * Uses the fetch API to stream data from the server.
 * The callback function is executed for each chunk of data received.
 */
const stream = async (props, url, headers, callback = null) => {
    const response = await fetch(url, { headers });

    // If unauthorized, most likely the JWT has expired.
    if (response.status === 401) {
        response.body?.cancel("Authentication failed");
        sessionExpired(props);
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let result = "";

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        result += chunk;

        if (callback) callback(chunk);
    }

    return result;
};

/**
 * A standardized GET query, which streams the data.
 * Includes a path and a callback function.
 * The callback function is executed for each chunk of data received.
 */
export const streamGetQuery = async (props, path, callback, finalCallback, options = {}) => {
    const jwt = props.user.userJWT;
    let { url, headers } = createRequestVariables(path, jwt, options);

    await stream(props, url, headers, callback)
        .then(function (response) {
            finalCallback(response);
        })
        .catch((err) => {
            catchAPIError(props, err, options);
            finalCallback(null);
        });
};

/**
 * A standardized GET query.
 * Includes a path and a callback function.
 *
 * If interval is specified, periodically query the data.
 * If condition function is defined, query periodically only if said condition is satisfied.
 *
 * Options:
 * - interval: the interval in seconds to query the data.
 * - condition: a function that returns a boolean. If true, query the data.
 * - headers: custom headers to add to the request.
 * - customColor: a custom color for the snackbar.
 * - customMessage: a custom message for the snackbar.
 * - showMessage: whether to show the snackbar message.
 * - ip: a new IP address to replace the current one.
 * - port: a new port to replace the current one.
 */
export const standardGetQuery = (props, path, callback, options = {}) => {
    const { interval, condition } = options;

    const fetchData = () => {
        const jwt = props.user.userJWT; // NB! JWT state won't be in case of interval queries.

        getFetch(path, jwt, options)
            .then(function (response) {
                if (response.ok) {
                    callback(response);
                } else {
                    catchAPIResponse(props, response, options);
                    options.onError?.(response);
                }
            })
            .catch((err) => {
                catchAPIError(props, err, options);
                options.onError?.(err);
            });
    };

    // Initial query.
    fetchData();

    // Query if condition is satisfied.
    const conditionalQuery = () => {
        if (condition) {
            const fulfilled = condition();

            if (fulfilled) {
                fetchData();
            }
        } else {
            fetchData();
        }
    };

    // Periodic query if interval is provided.
    if (interval) {
        const loop = setInterval(conditionalQuery, interval * 1000);

        // Return a function to clear the interval when needed.
        return () => clearInterval(loop);
    }
};

/**
 * A standardized POST query.
 * Includes a path and a callback function.
 */
export const standardPostQuery = async (props, path, body, callback, options) => {
    await postFetch(path, body, props.user.userJWT)
        .then(function (response) {
            if (response.ok) {
                callback(response);
            } else {
                catchAPIResponse(props, response, options);
            }
        })
        .catch((err) => catchAPIError(props, err, options));
};

/**
 * A standardized PUT query.
 * Includes a path and a callback function.
 */
export const standardPutQuery = async (props, path, body, callback) => {
    await putFetch(path, body, props.user.userJWT)
        .then(function (response) {
            if (response.ok) {
                callback(response);
            } else {
                catchAPIResponse(props, response);
            }
        })
        .catch((err) => catchAPIError(props, err));
};

/**
 * A standardized DELETE query.
 * Includes a path and a callback function.
 */
export const standardDeleteQuery = async (props, path, body, callback) => {
    await deleteFetch(path, body, props.user.userJWT)
        .then(function (response) {
            if (response.ok) {
                callback(response);
            } else {
                catchAPIResponse(props, response);
            }
        })
        .catch((err) => catchAPIError(props, err));
};

/**
 * Simulate an HTTP query.
 * Used for testing purposes.
 */
export const simulationQuery = async (props, path, body, callback) => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    callback({ body: { success: true } });
};

/**
 * A standardized GET query.
 * Includes a path and a callback function.
 */
export const standardFileQuery = async (props, path, callback, options) => {
    await getFetch(path, props.user.userJWT, options)
        .then(function (response) {
            if (response.ok) {
                callback(response);
            } else {
                catchAPIResponse(props, response, options);
            }
        })
        .catch((err) => catchAPIError(props, err, options));
};

/**
 * Main fetch function.
 * Used by all other fetch functions.
 * Returns the response body and status code.
 *
 * If the response is not received within 30 seconds, the request is aborted.
 */
async function fetchFromBackend(url, HTTPmethod, HTTPheaders, HTTPbody) {
    try {
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), Constants.HTTP_QUERY_TIMEOUT_MS);

        const response = await Promise.race([
            fetch(url, {
                method: HTTPmethod,
                headers: HTTPheaders,
                body: typeof HTTPbody === "object" ? JSON.stringify(HTTPbody) : HTTPbody,

                signal: controller.signal
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error("Request timed out")), Constants.HTTP_QUERY_TIMEOUT_MS))
        ]);

        clearTimeout(id);

        const contentType = response.headers.get("Content-Type");

        let data;

        try {
            if (contentType && contentType.includes("application/json")) {
                data = await response.json();
            } else if (contentType && (contentType.includes("text/plain") || contentType.includes("text/html"))) {
                data = await response.text();
            } else if (
                contentType &&
                (contentType.includes("application/octet-stream") || contentType.includes("image/") || contentType.includes("audio/") || contentType.includes("video/"))
            ) {
                data = await response.blob();
            } else {
                data = undefined;
            }
        } catch (e) {
            data = undefined;
        }

        return { ok: response.ok, body: data, status: response.status };
    } catch (e) {
        return { ok: false, body: undefined, status: 0 };
    }
}

/**
 * Perform a GET request.
 *
 * Parameters include the URL path and the authorization header (optional).
 * After receiving a response, convert into:
 * { ok: bool, body: [ {...} ] }
 */
export const getFetch = async (path, jwt, options) => {
    let { url, headers } = createRequestVariables(path, jwt, options);

    return fetchFromBackend(url, "GET", headers);
};

/**
 * Perform a POST request.
 *
 * Parameters include the URL path, the HTTP body, and the authorization header (optional).
 * After receiving a response, convert into:
 * { ok: bool, body: [ {...} ] }
 */
export const postFetch = async (path, body, jwt) => {
    let { url, headers } = createRequestVariables(path, jwt);

    return fetchFromBackend(url, "POST", headers, body);
};

/**
 * Perform a PUT request.
 *
 * Parameters include the URL path, the HTTP body, and the authorization header (optional).
 * After receiving a response, convert into:
 * { ok: bool, body: [ {...} ] }
 */
export const putFetch = async (path, body, jwt) => {
    let { url, headers } = createRequestVariables(path, jwt);

    return fetchFromBackend(url, "PUT", headers, body);
};

/**
 * Perform a PATCH request.
 *
 * Parameters include the URL path, the HTTP body, and the authorization header (optional).
 * After receiving a response, convert into:
 * { ok: bool, body: [ {...} ] }
 */
export const patchFetch = async (path, body, jwt) => {
    let { url, headers } = createRequestVariables(path, jwt);

    return fetchFromBackend(url, "PATCH", headers, body);
};

/**
 * Perform a DELETE request.
 *
 * Parameters include the URL path, the HTTP body, and the authorization header (optional).
 * After receiving a response, convert into:
 * { ok: bool, body: [ {...} ] }
 */
export const deleteFetch = async (path, body, jwt) => {
    let { url, headers } = createRequestVariables(path, jwt);

    return fetchFromBackend(url, "DELETE", headers, body);
};

/**
 * Return the URL and headers.
 */
const createRequestVariables = (path, jwt, options = {}) => {
    const url = createUrl({ ...options, path });
    const headers = {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: jwt ? "Bearer " + jwt : "",
        ...options.headers // Spread the headers from options.
    };

    return { url, headers };
};

/**
 * Return a URL.
 * In case of optional newUrl parameter, the current IP is replaced with the new one.
 */
export const createUrl = (options = {}) => {
    const { ip, port, path } = options;

    const newIp = ip ? window.location.protocol + "//" + ip : Constants.SERVER_IP;
    const newPort = port ? ":" + port : Constants.SERVER_PORT;

    return newIp + newPort + (path ?? "");
};

/**
 * Deal with errors.
 * Executed when the server successfully responds, but the response code is not 200.
 */
export const catchAPIResponse = (props, response, options = {}) => {
    const { customColor, customMessage, showMessage = true, checkExpired = true } = options;

    const status = response?.status ?? "UNKN";
    const message = response?.body?.detail?.[0]?.msg || response?.body?.detail || customMessage || getLanguage().SNACKBAR_ERROR_RESPONSE;
    const color = customColor ?? "error";

    console.warn("Non-successful status code:", response);

    try {
        if (showMessage) props.showSnackbar({ color, duration: 6000, message: `Error (${status}): ${message}` });
        // If unauthorized, most likely the JWT has expired.
        if (checkExpired && response.status === 401) sessionExpired(props);
    } catch (e) {
        console.warn(e);
    }
};

/**
 * Deal with errors.
 */
export const catchAPIError = (props, err, options = {}) => {
    const { customMessage, showMessage = true } = options;

    console.error("API error:", err);
    if (!showMessage) return;

    const message = customMessage ?? getLanguage().SNACKBAR_ERROR_SERVER;

    try {
        props.showSnackbar({ color: "error", duration: 6000, message });
    } catch (e) {
        console.error(e);
    }
};

/**
 * Return a string with the query parameters for the feedbacks.
 *
 * E.g. "?page=0&pageSize=10&orderBy=asset&order=asc&column=asset&operator=equals&value=TSLA"
 */
export const createCriteriaParameters = (options = {}) => {
    const { page, pageSize, filter, sort } = options;
    let queryParams = "";

    if (page !== undefined) queryParams += `&page=${page}`;
    if (pageSize !== undefined) queryParams += `&page_size=${pageSize}`;
    if (filter?.quickFilterValues && filter.quickFilterValues.length !== 0) queryParams += `&search=${encodeURIComponent(filter.quickFilterValues.join(","))}`;
    if (filter?.items && filter.items.length !== 0) queryParams += getFilterParameters(filter.items);
    if (sort && sort.length !== 0) queryParams += getSortParameters(sort);

    if (queryParams === "") return "";
    if (queryParams.startsWith("&")) queryParams = queryParams.substring(1);

    return `?${queryParams}`;
};

/**
 * Get the sort parameters by sort array.
 *
 * E.g. "orderBy=asset&order=asc"
 */
const getSortParameters = (sort) => {
    return `&order_by=${encodeURIComponent(sort[0]?.field)}&order=${encodeURIComponent(sort[0]?.sort)}`;
};

/**
 * Get the filter parameters by filter.items array
 *
 * E.g. "column=asset&operator=equals&value=TSLA"
 */
const getFilterParameters = (items = []) => {
    let queryParams = "";

    items.forEach((item) => {
        const { columnField, operatorValue, value } = item;

        if ([undefined, ""].includes(value) && !["isEmpty", "isNotEmpty"].includes(operatorValue)) return;

        let itemParams = `column=${encodeURIComponent(columnField)}&operator=${encodeURIComponent(operatorValue)}`;

        if (value) {
            if (isArray(value)) {
                itemParams += `&value=${value.map((v) => encodeURIComponent(v)).join(",")}`;
            } else {
                itemParams += `&value=${encodeURIComponent(value)}`;
            }
        }

        queryParams += `&${itemParams}`;
    });

    return queryParams;
};
